/**
 * FormulaAnalysisView — full-page Creative Formula Intelligence drill-down.
 *
 * Sections:
 *   1. Hero KPI strip  — top hook/formula/LTV value/coverage
 *   2. Dimension Trio  — Hook | Tone | Visual bar charts side-by-side
 *   3. Formula Leaderboard — ranked (hook·tone·visual) combos with in-row ad list
 *   4. AI Action Recommendations sidebar — MCP-seeded action cards
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Layers,
  Trophy,
  Zap,
  Target,
  FileText,
  AlertTriangle,
  Eye,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  TrendingUp,
  MessageCircle,
  Sparkles,
} from 'lucide-react';
import { clsx } from 'clsx';
import {
  creativeWorkbenchApi,
  type WorkbenchFormulaLeaderboardResponse,
  type WorkbenchFormulaCombo,
  type WorkbenchFormulaAction,
  type WorkbenchFormulaBreakdownResponse,
  type WorkbenchFormulaDimension,
  type WorkbenchFormulaBar,
} from '@/services/api';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';
import { EmbeddedChatPanel, type ChatSeedInput } from './EmbeddedChatPanel';

// ─── Constants ────────────────────────────────────────────────────────────────

const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
const COMPACT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

const VERDICT_BADGE: Record<string, string> = {
  strong: 'bg-teal-100 text-teal-700 ring-teal-200',
  promising: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
  weak: 'bg-slate-100 text-slate-500 ring-slate-200',
  untagged: 'bg-amber-50 text-amber-600 ring-amber-200',
  misaligned: 'bg-red-100 text-red-600 ring-red-200',
  needs_work: 'bg-amber-100 text-amber-600 ring-amber-200',
};

const VERDICT_BAR: Record<string, string> = {
  strong: 'bg-teal-500',
  promising: 'bg-emerald-400',
  weak: 'bg-slate-300',
  untagged: 'bg-amber-200',
};

const ACTION_ICON: Record<string, JSX.Element> = {
  budget: <TrendingUp size={14} className="text-teal-500" />,
  brief: <FileText size={14} className="text-indigo-500" />,
  diagnose: <AlertTriangle size={14} className="text-amber-500" />,
  coverage: <Eye size={14} className="text-slate-500" />,
};

const ACTION_COLORS: Record<string, string> = {
  budget: 'border-teal-200 bg-teal-50/50',
  brief: 'border-indigo-200 bg-indigo-50/50',
  diagnose: 'border-amber-200 bg-amber-50/50',
  coverage: 'border-slate-200 bg-slate-50',
};

// ─── Hero KPI strip ───────────────────────────────────────────────────────────

function FormulaKpiStrip({ data }: { data: WorkbenchFormulaLeaderboardResponse }) {
  const tagged = data.combos.filter((c) => c.verdict !== 'untagged');
  const topRoas = tagged.length ? tagged.reduce((a, b) => (a.ltv_roas > b.ltv_roas ? a : b)) : null;
  const topValue = tagged.length ? tagged.reduce((a, b) => (a.estimated_value > b.estimated_value ? a : b)) : null;
  const taggedPct =
    data.total_spend > 0 ? Math.round((data.tagged_spend / data.total_spend) * 100) : 0;

  const kpis = [
    {
      label: 'Best Formula',
      value: topValue?.formula.split(' · ')[0] ?? '—',
      sub: topValue ? `${topValue.formula} · ${CURRENCY.format(topValue.estimated_value)} LTV` : '',
      icon: <Trophy size={14} className="text-teal-500" />,
    },
    {
      label: 'Highest LTV-ROAS',
      value: topRoas ? `${topRoas.ltv_roas.toFixed(1)}x` : '—',
      sub: topRoas?.formula ?? '',
      icon: <TrendingUp size={14} className="text-emerald-500" />,
    },
    {
      label: 'Total LTV Value',
      value: COMPACT.format(data.total_ltv),
      sub: `${tagged.length} tagged formula${tagged.length !== 1 ? 's' : ''}`,
      icon: <Zap size={14} className="text-teal-500" />,
    },
    {
      label: 'Formula Coverage',
      value: `${taggedPct}%`,
      sub: `${COMPACT.format(data.tagged_spend)} of ${COMPACT.format(data.total_spend)} spend tagged`,
      icon: <Eye size={14} className="text-slate-500" />,
    },
    {
      label: 'Formula Combos',
      value: String(tagged.length),
      sub: 'distinct hook·tone·visual patterns',
      icon: <Layers size={14} className="text-indigo-500" />,
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {kpis.map((k) => (
        <div
          key={k.label}
          className="bg-white border border-slate-200 rounded-xl p-3 space-y-1"
        >
          <div className="flex items-center justify-between text-slate-400 mb-1">
            <span className="text-[10px] font-mono uppercase tracking-wide text-slate-500">
              {k.label}
            </span>
            {k.icon}
          </div>
          <div className="text-xl font-bold text-navy-900 truncate" title={k.value}>
            {k.value}
          </div>
          <p className="text-[10px] text-slate-500 truncate" title={k.sub}>
            {k.sub}
          </p>
        </div>
      ))}
    </div>
  );
}

// ─── Dimension Trio ───────────────────────────────────────────────────────────

const TRIO_DIMS: { key: WorkbenchFormulaDimension; label: string }[] = [
  { key: 'hook_type', label: 'Hook Type' },
  { key: 'tone', label: 'Tone' },
  { key: 'visual_style', label: 'Visual Style' },
];

function DimBarMini({ bar }: { bar: WorkbenchFormulaBar }) {
  return (
    <div className="group flex items-center gap-2">
      <span className="text-[10px] capitalize text-slate-600 w-24 shrink-0 truncate" title={bar.label}>
        {bar.label}
      </span>
      <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={clsx(
            'h-full rounded-full',
            bar.strength === 'strong'
              ? 'bg-teal-500'
              : bar.strength === 'promising'
              ? 'bg-emerald-400'
              : bar.label === 'unknown'
              ? 'bg-slate-200'
              : 'bg-slate-300',
          )}
          style={{ width: `${Math.max(bar.bar_width_pct, 2)}%` }}
        />
      </div>
      <div className="text-[10px] font-mono text-right shrink-0 space-y-0">
        <span className="block text-slate-700 font-bold">
          {bar.estimated_value > 0 ? COMPACT.format(bar.estimated_value) : COMPACT.format(bar.spend)}
        </span>
        <span className="block text-slate-400">{bar.ctr.toFixed(1)}% CTR</span>
      </div>
    </div>
  );
}

function DimensionCard({
  dim,
  data,
}: {
  dim: { key: WorkbenchFormulaDimension; label: string };
  data: WorkbenchFormulaBreakdownResponse | null;
}) {
  const bars = (data?.bars ?? []).filter((b) => b.label !== 'unknown').slice(0, 6);
  const winner = data?.winner;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 flex-1 min-w-0">
      <div className="flex items-center justify-between">
        <h3 className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">
          {dim.label}
        </h3>
        {winner && (
          <span className="text-[10px] bg-teal-50 text-teal-700 ring-1 ring-teal-200 rounded-full px-2 py-0.5 font-semibold capitalize">
            {winner}
          </span>
        )}
      </div>
      {!data && <div className="text-xs text-slate-400">Loading...</div>}
      <div className="space-y-1.5">
        {bars.map((b) => (
          <DimBarMini key={b.label} bar={b} />
        ))}
      </div>
    </div>
  );
}

function DimensionTrio() {
  const { clientUid, personaId } = useCreativeWorkbench();
  const [byDim, setByDim] = useState<Partial<Record<WorkbenchFormulaDimension, WorkbenchFormulaBreakdownResponse>>>({});

  useEffect(() => {
    if (!clientUid) return;
    TRIO_DIMS.forEach(({ key }) => {
      creativeWorkbenchApi
        .getFormulaBreakdown({ clientUid, dimension: key, personaId })
        .then((d) => setByDim((prev) => ({ ...prev, [key]: d })))
        .catch(() => {});
    });
  }, [clientUid, personaId]);

  return (
    <div className="flex gap-4 flex-col xl:flex-row">
      {TRIO_DIMS.map((dim) => (
        <DimensionCard key={dim.key} dim={dim} data={byDim[dim.key] ?? null} />
      ))}
    </div>
  );
}

// ─── Formula Leaderboard ──────────────────────────────────────────────────────

function ComboRow({
  combo,
  rank,
  onDrillDown,
}: {
  combo: WorkbenchFormulaCombo;
  rank: number;
  onDrillDown: (s: ChatSeedInput) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden">
      {/* Main row */}
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-white hover:bg-slate-50 transition-colors text-left"
      >
        {/* Rank */}
        <span
          className={clsx(
            'w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold',
            rank === 1
              ? 'bg-teal-100 text-teal-700'
              : rank === 2
              ? 'bg-slate-200 text-slate-600'
              : rank === 3
              ? 'bg-amber-100 text-amber-700'
              : 'bg-slate-100 text-slate-500',
          )}
        >
          {rank}
        </span>

        {/* Formula pills */}
        <div className="flex items-center gap-1 flex-1 min-w-0 flex-wrap">
          {[combo.hook_type, combo.tone, combo.visual_style].map((part, i) => (
            <span
              key={i}
              className="text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5 font-mono capitalize"
            >
              {part}
            </span>
          ))}
          <span
            className={clsx(
              'text-[9px] font-bold rounded-full px-1.5 py-0.5 ring-1 capitalize ml-1',
              VERDICT_BADGE[combo.verdict] ?? VERDICT_BADGE.weak,
            )}
          >
            {combo.verdict}
          </span>
        </div>

        {/* Bar */}
        <div className="hidden sm:block w-24 h-2 bg-slate-100 rounded-full overflow-hidden shrink-0">
          <div
            className={clsx(
              'h-full rounded-full',
              VERDICT_BAR[combo.verdict] ?? 'bg-slate-300',
            )}
            style={{ width: `${Math.max(combo.bar_width_pct, 2)}%` }}
          />
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-4 gap-4 shrink-0 text-right">
          <div>
            <div className="text-[10px] text-slate-400">Ads</div>
            <div className="text-[11px] font-bold text-slate-700">{combo.ad_count}</div>
          </div>
          <div>
            <div className="text-[10px] text-slate-400">Spend</div>
            <div className="text-[11px] font-bold text-slate-700">{COMPACT.format(combo.spend)}</div>
          </div>
          <div>
            <div className="text-[10px] text-slate-400">LTV-ROAS</div>
            <div className={clsx(
              'text-[11px] font-bold',
              combo.ltv_roas >= 2.0 ? 'text-teal-600' : combo.ltv_roas >= 0.5 ? 'text-emerald-600' : 'text-slate-500',
            )}>
              {combo.ltv_roas.toFixed(2)}x
            </div>
          </div>
          <div>
            <div className="text-[10px] text-slate-400">Est. LTV</div>
            <div className="text-[11px] font-bold text-teal-700">{CURRENCY.format(combo.estimated_value)}</div>
          </div>
        </div>

        {/* Expand chevron */}
        {expanded ? (
          <ChevronDown size={14} className="text-slate-400 shrink-0" />
        ) : (
          <ChevronRight size={14} className="text-slate-400 shrink-0" />
        )}
      </button>

      {/* Expanded: CTR/CVR detail + top ads */}
      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50 px-4 py-3 space-y-3">
          {/* Secondary metrics row */}
          <div className="flex items-center gap-6 text-[11px]">
            <span className="text-slate-500">
              CTR: <strong className="text-slate-700">{combo.ctr.toFixed(2)}%</strong>
            </span>
            <span className="text-slate-500">
              CVR: <strong className="text-slate-700">{combo.cvr.toFixed(2)}%</strong>
            </span>
            <span className="text-slate-500">
              Visitors: <strong className="text-slate-700">{combo.visitors.toLocaleString()}</strong>
            </span>
            <span className="text-slate-500">
              Avg LTV: <strong className="text-slate-700">{CURRENCY.format(combo.avg_ltv)}</strong>
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDrillDown({
                  question: `Deep dive into the '${combo.formula}' creative formula. Why does it perform the way it does and what should we do differently?`,
                  chart_axis: { series: 'formula', dimension: 'combo', formula: combo.formula },
                });
              }}
              className="ml-auto flex items-center gap-1 text-[10px] text-teal-600 hover:text-teal-700 font-semibold"
            >
              <MessageCircle size={10} />
              Ask analyst
            </button>
          </div>

          {/* Top ads for this formula */}
          {combo.ads.length > 0 && (
            <div>
              <div className="text-[10px] font-mono text-slate-400 uppercase mb-1.5">
                Top performing ads with this formula
              </div>
              <div className="space-y-1">
                {combo.ads.map((ad) => (
                  <div
                    key={ad.ad_id}
                    className="flex items-center gap-3 text-[11px] bg-white rounded-lg border border-slate-100 px-3 py-2"
                  >
                    {/* Fit verdict */}
                    <span
                      className={clsx(
                        'text-[9px] font-bold rounded-full px-1.5 py-0.5 ring-1 capitalize shrink-0',
                        VERDICT_BADGE[ad.verdict] ?? VERDICT_BADGE.weak,
                      )}
                    >
                      {ad.verdict}
                    </span>

                    {/* Ad name */}
                    <span className="flex-1 min-w-0 text-slate-700 truncate font-medium" title={ad.ad_name ?? ''}>
                      {ad.ad_name ?? ad.ad_id}
                    </span>

                    {/* Persona */}
                    <span className="text-[10px] text-slate-400 hidden md:block shrink-0 max-w-[120px] truncate" title={ad.target_persona}>
                      {ad.target_persona}
                    </span>

                    {/* Metrics */}
                    <div className="flex items-center gap-4 text-right shrink-0">
                      <span className="text-slate-500">{COMPACT.format(ad.spend)}</span>
                      <span className="text-[10px] font-mono text-slate-400">{ad.ctr.toFixed(1)}% CTR</span>
                      <span className={clsx(
                        'font-bold',
                        ad.ltv_roas >= 2 ? 'text-teal-600' : ad.ltv_roas >= 0.5 ? 'text-emerald-600' : 'text-slate-500',
                      )}>
                        {ad.ltv_roas.toFixed(2)}x
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FormulaLeaderboard({
  data,
  onDrillDown,
}: {
  data: WorkbenchFormulaLeaderboardResponse;
  onDrillDown: (s: ChatSeedInput) => void;
}) {
  const combos = data.combos.filter((c) => c.verdict !== 'untagged');
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-navy-900 flex items-center gap-1.5">
          <Trophy size={14} className="text-teal-500" />
          Formula Combo Leaderboard
        </h2>
        <span className="text-[10px] text-slate-400 font-mono">
          ranked by est. LTV value · click to expand
        </span>
      </div>

      <div className="space-y-2">
        {combos.map((combo, idx) => (
          <ComboRow
            key={combo.formula}
            combo={combo}
            rank={idx + 1}
            onDrillDown={onDrillDown}
          />
        ))}
        {combos.length === 0 && (
          <div className="text-xs text-slate-400 text-center py-6">
            No tagged formula combos yet.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Action Recommendations ───────────────────────────────────────────────────

function ActionCard({
  action,
  onActivate,
  active,
}: {
  action: WorkbenchFormulaAction;
  onActivate: (seed: ChatSeedInput) => void;
  active: boolean;
}) {
  return (
    <div
      className={clsx(
        'border rounded-xl p-4 space-y-2.5 transition-all',
        ACTION_COLORS[action.type] ?? 'border-slate-200 bg-white',
        active && 'ring-2 ring-teal-400',
      )}
    >
      <div className="flex items-start gap-2">
        <div className="mt-0.5 shrink-0">{ACTION_ICON[action.type]}</div>
        <div className="min-w-0">
          <div className="text-[12px] font-bold text-slate-800 leading-snug">
            {action.title}
          </div>
        </div>
      </div>

      <p className="text-[11px] text-slate-600 leading-relaxed">
        {action.rationale}
      </p>

      <button
        onClick={() =>
          onActivate({
            question: action.chat_seed.question,
            chart_axis: action.chat_seed.chart_axis,
          })
        }
        className={clsx(
          'w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] font-semibold transition-colors',
          active
            ? 'bg-teal-500 text-white'
            : 'bg-white border border-slate-200 text-slate-700 hover:border-teal-400 hover:text-teal-700',
        )}
      >
        <MessageCircle size={11} />
        {active ? 'Analyst open ↓' : 'Open in analyst'}
        {!active && <ArrowRight size={10} />}
      </button>
    </div>
  );
}

function ActionRecommendations({
  actions,
  onActivate,
  activeSeed,
}: {
  actions: WorkbenchFormulaAction[];
  onActivate: (seed: ChatSeedInput) => void;
  activeSeed: ChatSeedInput | null;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Sparkles size={13} className="text-teal-500" />
        <h2 className="text-[12px] font-bold text-navy-900">Recommended Actions</h2>
        <span className="text-[10px] text-slate-400">powered by Customer Agent MCP</span>
      </div>
      <p className="text-[11px] text-slate-500">
        Each recommendation is derived from the real data above. Click any to open
        a pre-loaded analyst session.
      </p>
      <div className="space-y-3">
        {actions.map((action) => (
          <ActionCard
            key={action.id}
            action={action}
            onActivate={onActivate}
            active={activeSeed?.question === action.chat_seed.question}
          />
        ))}
        {actions.length === 0 && (
          <div className="text-xs text-slate-400 text-center py-4">
            No recommendations yet — add formula tags to more ads.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Root view ────────────────────────────────────────────────────────────────

export function FormulaAnalysisView() {
  const navigate = useNavigate();
  const { clientUid } = useCreativeWorkbench();
  const [data, setData] = useState<WorkbenchFormulaLeaderboardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [chatSeed, setChatSeed] = useState<ChatSeedInput | null>(null);

  useEffect(() => {
    if (!clientUid) return;
    let cancelled = false;
    setLoading(true);
    creativeWorkbenchApi
      .getFormulaLeaderboard({ clientUid })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientUid]);

  return (
    <div className="space-y-5">
      {/* Page header */}
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/creative/performance')}
            className="p-1.5 text-slate-500 hover:text-teal-600 rounded hover:bg-slate-100"
            aria-label="Back to Performance"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <div className="text-[10px] font-mono uppercase text-slate-400">
              Performance › Creative Formula Intelligence
            </div>
            <h1 className="text-xl font-bold text-navy-900 flex items-center gap-2">
              <Layers size={18} className="text-teal-500" />
              Formula Analysis
            </h1>
          </div>
        </div>
        <p className="text-[11px] text-slate-500 italic max-w-sm text-right hidden md:block">
          Which creative formulas are actually steering Meta to acquire
          high-LTV customers — and what to do about it.
        </p>
      </header>

      {/* Loading skeleton */}
      {loading && !data && (
        <div className="text-sm text-slate-400 animate-pulse py-12 text-center">
          Loading formula intelligence...
        </div>
      )}

      {data && (
        <>
          {/* 1. Hero KPIs */}
          <FormulaKpiStrip data={data} />

          {/* 2. Dimension trio */}
          <section className="space-y-2">
            <div className="flex items-center gap-2">
              <Target size={13} className="text-teal-500" />
              <h2 className="text-sm font-bold text-navy-900">Dimension Breakdown</h2>
              <span className="text-[10px] text-slate-400">LTV value per creative attribute</span>
            </div>
            <DimensionTrio />
          </section>

          {/* 3. Main body: Leaderboard + Sidebar */}
          <div className="grid grid-cols-12 gap-5">
            {/* Leaderboard */}
            <div className={clsx(
              'space-y-4',
              chatSeed ? 'col-span-12 lg:col-span-8' : 'col-span-12 lg:col-span-8',
            )}>
              <FormulaLeaderboard data={data} onDrillDown={setChatSeed} />
            </div>

            {/* Sidebar: Actions + embedded chat */}
            <aside className="col-span-12 lg:col-span-4 space-y-4">
              <ActionRecommendations
                actions={data.actions}
                onActivate={setChatSeed}
                activeSeed={chatSeed}
              />

              {chatSeed && (
                <EmbeddedChatPanel
                  seed={chatSeed}
                  onClose={() => setChatSeed(null)}
                />
              )}
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
